// Generic renderer for the server-built `land_map` config (see the contract in
// atlas-backend/docs/property-context/atlas-packet/). The server emits fully
// styled GeoJSON layers; this module only turns them into Mapbox sources and
// layers. No React and no mapbox-gl import — the caller owns the map and the
// popup instance, which keeps these builders unit-testable with a fake map.
//
// Layer kinds:
//   polygon → `<src>-fill` [+ `<src>-casing`] + `<src>-line` [+ `<src>-line-crossing`]
//   line    → [`<src>-fill` when style.fill is given] + `<src>-line`
//   point   → `<src>-circle` [+ `<src>-label` when style.show_labels]
// Fill/line layers go before `beforeId` (the basemap's first symbol layer) so
// street labels stay readable; circles and our own labels go on top.

export const BASEMAP_STYLES = {
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  streets: 'mapbox://styles/mapbox/streets-v12',
  light: 'mapbox://styles/mapbox/light-v11',
};

export const LAND_MAP_FIT_PADDING = 40;
export const LAND_MAP_FIT_MAX_ZOOM = 18.3;

const SOURCE_PREFIX = 'land-';
const DEFAULT_POINT_COLOR = '#d2691e';
const DEFAULT_POINT_RADIUS = 6;
const DEFAULT_STROKE = '#ffffff';
const DEFAULT_STROKE_WIDTH = 1.5;
const DEFAULT_LABEL_COLOR = '#111827';
const LABEL_HALO = '#ffffff';
const LEGEND_MAX_ITEMS = 16;

/** Mapbox style URL for a basemap name; an explicit `mapbox://` URL passes through; unknown/missing → light. */
export function basemapStyleUrl(name) {
  if (typeof name === 'string' && name.startsWith('mapbox://')) return name;
  const key = typeof name === 'string' ? name.trim().toLowerCase() : '';
  return BASEMAP_STYLES[key] || BASEMAP_STYLES.light;
}

const num = (v, fallback) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : fallback);
const isNonEmptyArray = (v) => Array.isArray(v) && v.length > 0;

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ---------------------------------------------------------------------------
// Bounds
// ---------------------------------------------------------------------------

function normalizeBounds(bounds) {
  if (!Array.isArray(bounds) || bounds.length !== 2) return null;
  const [a, b] = bounds;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) return null;
  const vals = [Number(a[0]), Number(a[1]), Number(b[0]), Number(b[1])];
  if (!vals.every(Number.isFinite)) return null;
  const [x1, y1, x2, y2] = vals;
  return [[Math.min(x1, x2), Math.min(y1, y2)], [Math.max(x1, x2), Math.max(y1, y2)]];
}

function walkCoordinates(coords, visit) {
  if (!Array.isArray(coords)) return;
  if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    visit(coords[0], coords[1]);
    return;
  }
  coords.forEach((c) => walkCoordinates(c, visit));
}

function walkGeometry(geometry, visit) {
  if (!geometry || typeof geometry !== 'object') return;
  if (geometry.type === 'GeometryCollection') {
    (geometry.geometries || []).forEach((g) => walkGeometry(g, visit));
    return;
  }
  walkCoordinates(geometry.coordinates, visit);
}

function walkGeojson(geojson, visit) {
  if (!geojson || typeof geojson !== 'object') return;
  if (geojson.type === 'FeatureCollection') {
    (geojson.features || []).forEach((f) => walkGeometry(f && f.geometry, visit));
  } else if (geojson.type === 'Feature') {
    walkGeometry(geojson.geometry, visit);
  } else {
    walkGeometry(geojson, visit);
  }
}

/** [[minLng, minLat], [maxLng, maxLat]] from cfg.bounds, else the extent of every layer's geometry; null when empty. */
export function landMapBounds(cfg) {
  const explicit = normalizeBounds(cfg && cfg.bounds);
  if (explicit) return explicit;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const visit = (x, y) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  };
  const layers = Array.isArray(cfg && cfg.layers) ? cfg.layers : [];
  layers.forEach((layer) => {
    if (!layer) return;
    walkGeojson(layer.geojson, visit);
    (Array.isArray(layer.features) ? layer.features : []).forEach((f) => {
      if (!f) return;
      if (f.geometry) walkGeometry(f.geometry, visit);
      else visit(Number(f.lng), Number(f.lat));
    });
  });
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return [[minX, minY], [maxX, maxY]];
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

const LEGEND_SHAPES = { swatch: 'swatch', line: 'line', point: 'point' };

function layerLegendColor(layer) {
  const style = (layer && layer.style) || {};
  if (style.fill) return style.fill;
  if (style.line) return style.line;
  if (style.color) return style.color;
  const first = Array.isArray(layer && layer.features) ? layer.features.find((f) => f && f.color) : null;
  return first ? first.color : null;
}

function layerLegendShape(layer) {
  if (!layer) return 'swatch';
  if (layer.kind === 'point') return 'point';
  if (layer.kind === 'line') return 'line';
  return 'swatch';
}

/**
 * MapLegend config (`{ type: 'categorical', title, categories: [{ label, color, shape }], maxItems }`)
 * from cfg.legend; falls back to one entry per labelled layer. Null when there is nothing to show.
 */
export function legendItemsFromConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  let items = [];
  if (isNonEmptyArray(cfg.legend)) {
    items = cfg.legend
      .filter((it) => it && typeof it === 'object' && it.label && it.color)
      .map((it) => ({ label: String(it.label), color: String(it.color), shape: LEGEND_SHAPES[it.type] || 'swatch' }));
  } else if (isNonEmptyArray(cfg.layers)) {
    items = cfg.layers
      .filter((layer) => layer && layer.label)
      .map((layer) => ({ label: String(layer.label), color: layerLegendColor(layer), shape: layerLegendShape(layer) }))
      .filter((it) => it.color);
  }
  if (!items.length) return null;
  const title = cfg.legend_title || cfg.title || null;
  return { type: 'categorical', title, categories: items, maxItems: Math.min(items.length, LEGEND_MAX_ITEMS) };
}

// ---------------------------------------------------------------------------
// Style expressions
// ---------------------------------------------------------------------------

const IS_CROSSING = ['==', ['get', 'crosses_boundary'], true];
const IS_SURROUND = ['==', ['get', 'location'], 'surroundings'];

/**
 * Fill opacity by feature: crossing → fill_opacity_crossing, surroundings →
 * fill_opacity_surround, otherwise fill_opacity. A plain number when neither
 * variant is given so simple polygons need no expression.
 */
export function polygonFillOpacity(style = {}) {
  const base = num(style.fill_opacity, 0.3);
  const surround = num(style.fill_opacity_surround, null);
  const crossing = num(style.fill_opacity_crossing, null);
  if (surround === null && crossing === null) return base;
  return ['case', IS_CROSSING, crossing ?? surround ?? base, IS_SURROUND, surround ?? base, base];
}

function polygonLineOpacity(style = {}) {
  const base = num(style.line_opacity, 1);
  const surround = num(style.line_opacity_surround, null);
  if (surround === null) return base;
  return ['case', IS_SURROUND, surround, base];
}

function polygonLineWidth(style = {}) {
  const base = num(style.width, 1);
  const surround = num(style.width_surround, null);
  if (surround === null) return base;
  return ['case', IS_SURROUND, surround, base];
}

// ---------------------------------------------------------------------------
// Feature normalisation
// ---------------------------------------------------------------------------

function emptyCollection() {
  return { type: 'FeatureCollection', features: [] };
}

function asFeatureCollection(geojson) {
  if (!geojson || typeof geojson !== 'object') return emptyCollection();
  if (geojson.type === 'FeatureCollection') return { type: 'FeatureCollection', features: Array.isArray(geojson.features) ? geojson.features : [] };
  if (geojson.type === 'Feature') return { type: 'FeatureCollection', features: [geojson] };
  if (geojson.type && geojson.coordinates) return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: geojson }] };
  return emptyCollection();
}

function popupProps(popup, fallbackTitle) {
  const out = {};
  if (popup && typeof popup === 'object') {
    if (popup.title) out.popup_title = String(popup.title);
    if (isNonEmptyArray(popup.lines)) out.popup_lines = JSON.stringify(popup.lines.map((l) => String(l)));
  }
  if (!out.popup_title && fallbackTitle) out.popup_title = String(fallbackTitle);
  return out;
}

/** GeoJSON point features from a point layer's `features` ([{lng, lat, label, color, radius, popup}]) or its `geojson`. */
export function pointFeatureCollection(layer) {
  const style = (layer && layer.style) || {};
  if (isNonEmptyArray(layer && layer.features)) {
    const features = layer.features
      .map((p) => {
        if (!p) return null;
        const coords = p.geometry && p.geometry.type === 'Point' ? p.geometry.coordinates : [p.lng, p.lat];
        const lng = Number(coords && coords[0]);
        const lat = Number(coords && coords[1]);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        const props = { ...(p.properties || {}) };
        props.label = p.label != null ? String(p.label) : (props.label != null ? String(props.label) : '');
        props.color = p.color || props.color || style.color || DEFAULT_POINT_COLOR;
        props.radius = num(p.radius, num(props.radius, num(style.radius, DEFAULT_POINT_RADIUS)));
        Object.assign(props, popupProps(p.popup, props.label));
        return { type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: props };
      })
      .filter(Boolean);
    return { type: 'FeatureCollection', features };
  }
  const fc = asFeatureCollection(layer && layer.geojson);
  fc.features = fc.features.map((f) => {
    const props = { ...((f && f.properties) || {}) };
    if (props.label == null && props.name != null) props.label = String(props.name);
    if (!props.color) props.color = style.color || DEFAULT_POINT_COLOR;
    if (!Number.isFinite(Number(props.radius))) props.radius = num(style.radius, DEFAULT_POINT_RADIUS);
    Object.assign(props, popupProps(props.popup, props.label));
    return { ...f, properties: props };
  });
  return fc;
}

function polygonFeatureCollection(layer) {
  const fc = asFeatureCollection(layer && layer.geojson);
  fc.features = fc.features.map((f) => {
    const props = { ...((f && f.properties) || {}) };
    Object.assign(props, popupProps(props.popup, props.name || props.label));
    return { ...f, properties: props };
  });
  return fc;
}

// ---------------------------------------------------------------------------
// Popups
// ---------------------------------------------------------------------------

function safeJsonArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Popup HTML for a feature's properties: `popup_title` + `popup_lines`; empty string when there is nothing to say. */
export function popupHtml(props) {
  const p = props || {};
  const title = p.popup_title || p.label || '';
  const lines = safeJsonArray(p.popup_lines);
  if (!title && !lines.length) return '';
  const head = title ? `<div><strong>${escapeHtml(title)}</strong></div>` : '';
  const body = lines.map((l) => `<div>${escapeHtml(l)}</div>`).join('');
  return `${head}${body}`;
}

function attachPopup(map, layerId, popupRef) {
  if (typeof map.on !== 'function') return;
  const popup = () => (popupRef && popupRef.current) || null;
  const show = (evt) => {
    const feature = evt && evt.features && evt.features[0];
    const inst = popup();
    if (!feature || !inst) return;
    const html = popupHtml(feature.properties);
    if (!html) { inst.remove(); return; }
    inst.setLngLat(evt.lngLat).setHTML(html).addTo(map);
  };
  map.on('mouseenter', layerId, () => {
    const canvas = map.getCanvas && map.getCanvas();
    if (canvas) canvas.style.cursor = 'pointer';
  });
  map.on('mouseleave', layerId, () => {
    const canvas = map.getCanvas && map.getCanvas();
    if (canvas) canvas.style.cursor = '';
    const inst = popup();
    if (inst) inst.remove();
  });
  map.on('mousemove', layerId, show);
  map.on('click', layerId, show);
}

// ---------------------------------------------------------------------------
// Layer builders
// ---------------------------------------------------------------------------

function addOrUpdateSource(map, sourceId, data) {
  const existing = typeof map.getSource === 'function' ? map.getSource(sourceId) : null;
  if (existing) {
    if (typeof existing.setData === 'function') existing.setData(data);
    return false;
  }
  map.addSource(sourceId, { type: 'geojson', data });
  return true;
}

function addPolygonLayers(map, layer, sourceId, style, beforeId, popupRef) {
  const ids = [];
  const fillId = `${sourceId}-fill`;
  map.addLayer({
    id: fillId,
    type: 'fill',
    source: sourceId,
    paint: { 'fill-color': style.fill || style.line || '#888888', 'fill-opacity': polygonFillOpacity(style) },
  }, beforeId);
  ids.push(fillId);

  if (style.casing) {
    const casingId = `${sourceId}-casing`;
    map.addLayer({
      id: casingId,
      type: 'line',
      source: sourceId,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': style.casing,
        'line-width': num(style.casing_width, num(style.width, 1) + 2.5),
        'line-opacity': num(style.casing_opacity, 0.55),
      },
    }, beforeId);
    ids.push(casingId);
  }

  const lineColor = style.line || style.fill || '#888888';
  const hasCrossingDash = isNonEmptyArray(style.dash_crossing);
  const linePaint = {
    'line-color': lineColor,
    'line-width': polygonLineWidth(style),
    'line-opacity': polygonLineOpacity(style),
  };
  if (isNonEmptyArray(style.dash)) linePaint['line-dasharray'] = style.dash;
  const lineId = `${sourceId}-line`;
  map.addLayer({
    id: lineId,
    type: 'line',
    source: sourceId,
    ...(hasCrossingDash ? { filter: ['!=', ['get', 'crosses_boundary'], true] } : {}),
    layout: { 'line-join': 'round' },
    paint: linePaint,
  }, beforeId);
  ids.push(lineId);

  if (hasCrossingDash) {
    const crossingId = `${sourceId}-line-crossing`;
    map.addLayer({
      id: crossingId,
      type: 'line',
      source: sourceId,
      filter: IS_CROSSING,
      layout: { 'line-join': 'round' },
      paint: { ...linePaint, 'line-dasharray': style.dash_crossing },
    }, beforeId);
    ids.push(crossingId);
  }

  attachPopup(map, fillId, popupRef);
  return ids;
}

function addLineLayers(map, layer, sourceId, style, beforeId) {
  const ids = [];
  if (style.fill) {
    const fillId = `${sourceId}-fill`;
    map.addLayer({
      id: fillId,
      type: 'fill',
      source: sourceId,
      paint: { 'fill-color': style.fill, 'fill-opacity': num(style.fill_opacity, 0.06) },
    }, beforeId);
    ids.push(fillId);
  }
  const paint = {
    'line-color': style.line || style.fill || '#888888',
    'line-width': num(style.width, 1.5),
    'line-opacity': num(style.opacity, num(style.line_opacity, 1)),
  };
  if (isNonEmptyArray(style.dash)) paint['line-dasharray'] = style.dash;
  const lineId = `${sourceId}-line`;
  map.addLayer({
    id: lineId,
    type: 'line',
    source: sourceId,
    layout: { 'line-join': 'round', 'line-cap': 'round' },
    paint,
  }, beforeId);
  ids.push(lineId);
  return ids;
}

function addPointLayers(map, layer, sourceId, style, popupRef) {
  const ids = [];
  const circleId = `${sourceId}-circle`;
  map.addLayer({
    id: circleId,
    type: 'circle',
    source: sourceId,
    paint: {
      'circle-color': ['coalesce', ['get', 'color'], style.color || DEFAULT_POINT_COLOR],
      'circle-radius': ['to-number', ['coalesce', ['get', 'radius'], num(style.radius, DEFAULT_POINT_RADIUS)]],
      'circle-opacity': num(style.opacity, 0.92),
      'circle-stroke-color': style.stroke || DEFAULT_STROKE,
      'circle-stroke-width': num(style.stroke_width, DEFAULT_STROKE_WIDTH),
    },
  });
  ids.push(circleId);

  if (style.show_labels) {
    const labelId = `${sourceId}-label`;
    map.addLayer({
      id: labelId,
      type: 'symbol',
      source: sourceId,
      filter: ['!=', ['get', 'label'], ''],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': num(style.label_size, 11),
        'text-offset': [0, 1.0],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': style.label_color || DEFAULT_LABEL_COLOR,
        'text-halo-color': LABEL_HALO,
        'text-halo-width': 1.4,
      },
    });
    ids.push(labelId);
  }

  attachPopup(map, circleId, popupRef);
  return ids;
}

/** Layer ids this module would create for a layer entry (used to report existing ones on re-runs). */
function expectedLayerIds(layer, sourceId) {
  const style = (layer && layer.style) || {};
  if (layer.kind === 'point') return [`${sourceId}-circle`, ...(style.show_labels ? [`${sourceId}-label`] : [])];
  if (layer.kind === 'line') return [...(style.fill ? [`${sourceId}-fill`] : []), `${sourceId}-line`];
  return [
    `${sourceId}-fill`,
    ...(style.casing ? [`${sourceId}-casing`] : []),
    `${sourceId}-line`,
    ...(isNonEmptyArray(style.dash_crossing) ? [`${sourceId}-line-crossing`] : []),
  ];
}

/**
 * Add (or refresh) every layer of a `land_map` config on `map`, in config order.
 *
 * @param {object} map        mapbox-gl Map (or a fake with addSource/addLayer/getSource/on/getCanvas)
 * @param {object} cfg        the land_map config
 * @param {object} [options]
 * @param {string} [options.beforeId]  basemap layer id to insert fills/lines beneath (typically the first symbol layer)
 * @param {{current: object}} [options.popupRef]  ref holding a mapbox-gl Popup used for hover/click
 * @returns {string[]} ids of the layers managed for this config
 */
export function addLandMapLayers(map, cfg, { beforeId, popupRef } = {}) {
  if (!map || !cfg || !Array.isArray(cfg.layers)) return [];
  const ids = [];
  const seen = new Set();
  cfg.layers.forEach((layer, index) => {
    if (!layer || typeof layer !== 'object') return;
    const kind = layer.kind === 'point' || layer.kind === 'line' ? layer.kind : 'polygon';
    let rawId = layer.id != null && String(layer.id).trim() ? String(layer.id).trim() : `${kind}-${index}`;
    while (seen.has(rawId)) rawId = `${rawId}-${index}`;
    seen.add(rawId);
    const sourceId = `${SOURCE_PREFIX}${rawId}`;
    const style = layer.style && typeof layer.style === 'object' ? layer.style : {};

    const data = kind === 'point' ? pointFeatureCollection(layer) : polygonFeatureCollection(layer);
    const created = addOrUpdateSource(map, sourceId, data);
    if (!created) {
      ids.push(...expectedLayerIds({ ...layer, kind }, sourceId));
      return;
    }
    if (kind === 'point') ids.push(...addPointLayers(map, layer, sourceId, style, popupRef));
    else if (kind === 'line') ids.push(...addLineLayers(map, layer, sourceId, style, beforeId));
    else ids.push(...addPolygonLayers(map, layer, sourceId, style, beforeId, popupRef));
  });
  return ids;
}
